import React, { useEffect, useState } from 'react';
import { Text, View, Pressable } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import Input from '@/components/General/inputs/Input';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { saveTaskAssignmentTemplate } from '@/data/tasks';
import Toast from 'react-native-toast-message';
import SheetSelector from '@/components/General/inputs/SheetSelector';
import InputSection from '@/components/General/inputs/InputSection';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { Query } from '@/utils/react-query/Queries';
import ModalWrapper from '@/components/General/ModalWrapper';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import Avatar from '@/components/People/Avatar';
import Unicon from '@/components/General/Unicon';

export default function AddTaskTemplateModal() {
    const { id, teamID, prefill, defaultName } = useLocalSearchParams();
    const router = useRouter();
    const queryClient = useQueryClient();

    const [name, setName] = useState(defaultName || '');
    const [selectedTeam, setSelectedTeam] = useState(null);
    const [members, setMembers] = useState(() => {
        if (prefill && !id) {
            try {
                const parsed = JSON.parse(prefill);
                if (Array.isArray(parsed)) return parsed;
            } catch {
                // ignore invalid JSON
            }
        }
        return [];
    });

    const { data: templateToEdit } = Query.taskAssignmentTemplates.byId({ id: id, options: { enabled: !!id } });
    const { data: taskTeams } = Query.taskTeams.all();

    useEffect(() => {
        if (templateToEdit && taskTeams) {
            setName(templateToEdit.name || '');
            const fullTeam = taskTeams.find(t => t.id === templateToEdit.VolunteerTeam?.id) || templateToEdit.VolunteerTeam || null;
            setSelectedTeam(fullTeam);
            setMembers(templateToEdit.Members?.map(m => ({
                taskID: m.taskID,
                position: m.position,
                Person: m.Person || null,
            })) || []);
        }
    }, [templateToEdit, taskTeams]);

    useEffect(() => {
        if (teamID && taskTeams?.length && !selectedTeam) {
            const team = taskTeams.find(t => t.id === teamID);
            if (team) setSelectedTeam(team);
        }
    }, [teamID, taskTeams, selectedTeam]);

    const teamTasks = selectedTeam?.Tasks || [];

    const saveMutation = useMutation({
        mutationFn: saveTaskAssignmentTemplate,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QueryKeys.taskAssignmentTemplates.all() });
            Toast.show({ type: 'success', text2: 'Template saved successfully!' });
            router.back();
        },
    });

    const handleSave = () => {
        if (!name.trim()) {
            Toast.show({ type: 'error', text2: 'Template name is required' });
            return;
        }
        if (!selectedTeam?.id) {
            Toast.show({ type: 'error', text2: 'Please select a team' });
            return;
        }

        const validMembers = members
            .filter(m => m.Person?.id)
            .map((m, i) => ({
                taskID: m.taskID,
                personID: m.Person.id,
                position: m.position ?? i,
            }));

        saveMutation.mutate({
            id: id || undefined,
            name: name.trim(),
            volunteerTeamID: selectedTeam.id,
            members: validMembers,
        });
    };

    const getMembersForTask = (taskID) => {
        return members
            .filter(m => m.taskID === taskID)
            .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
    };

    const addMember = (taskID, person) => {
        if (!person?.id) return;
        const alreadyAssigned = members.some(
            m => m.taskID === taskID && m.Person?.id === person.id
        );
        if (alreadyAssigned) return;

        setMembers(prev => [
            ...prev,
            { taskID, Person: person, position: prev.length },
        ]);
    };

    const removeMember = (taskID, personID) => {
        setMembers(prev => prev.filter(
            m => !(m.taskID === taskID && m.Person?.id === personID)
        ));
    };

    return (
        <ModalWrapper
            title={id ? 'Edit Template' : 'New Template'}
            loading={saveMutation.isPending}
            onConfirm={handleSave}
        >
            <KeyboardAwareScrollView bottomOffset={100} keyboardShouldPersistTaps="handled">
                <View style={styles.paddingBottom}>
                    <InputSection>
                        <Input
                            title="Template Name"
                            autoCapitalize="words"
                            value={name}
                            onUpdate={setName}
                        />

                        <SheetSelector
                            type="taskTeams"
                            title="Team"
                            value={selectedTeam}
                            disabled={!!id}
                            onChange={(team) => {
                                setSelectedTeam(team);
                                setMembers([]);
                            }}
                        />
                    </InputSection>

                    {selectedTeam && (
                        <InputSection title="Assignments">
                            <Text style={styles.helpText}>
                                Choose people for each task in the team. Only assigned tasks will be included in the template.
                            </Text>

                            {teamTasks.map((task) => {
                                const taskMembers = getMembersForTask(task.id);
                                const assignedIds = taskMembers.map(m => m.Person?.id).filter(Boolean);

                                return (
                                    <View key={task.id} style={styles.taskRow}>
                                        <Text style={styles.taskName} numberOfLines={1}>{task.title}</Text>
                                        <View style={styles.assigneesContainer}>
                                            {taskMembers.map((m) => (
                                                <View key={m.Person.id} style={styles.assigneeChip}>
                                                    <Avatar size={20} profile={m.Person} onPress={() => {}} disableBorder />
                                                    <Text style={styles.assigneeName} numberOfLines={1}>
                                                        {m.Person.firstName} {m.Person.lastName}
                                                    </Text>
                                                    <Pressable
                                                        hitSlop={8}
                                                        onPress={() => removeMember(task.id, m.Person.id)}
                                                    >
                                                        <Unicon name="close" size={14} colorVariant="textSecondary" />
                                                    </Pressable>
                                                </View>
                                            ))}
                                            <SheetSelector
                                                type="people"
                                                title={`Add assignee for ${task.title}`}
                                                value={null}
                                                excludeIds={assignedIds}
                                                onChange={(p) => addMember(task.id, p)}
                                            >
                                                <View style={styles.addAssigneeChip}>
                                                    <Unicon name="add" size={16} colorVariant="primary" />
                                                    <Text style={styles.addAssigneeText}>Add</Text>
                                                </View>
                                            </SheetSelector>
                                        </View>
                                    </View>
                                );
                            })}
                        </InputSection>
                    )}
                </View>
            </KeyboardAwareScrollView>
        </ModalWrapper>
    );
}

const styles = StyleSheet.create((theme) => ({
    paddingBottom: {
        paddingBottom: 20,
    },
    helpText: {
        fontSize: 12,
        color: theme.colors.textSecondary,
        fontStyle: 'italic',
        marginBottom: 12,
    },
    taskRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingVertical: 10,
        borderBottomWidth: 1,
        borderBottomColor: theme.colors.border,
        gap: 12,
    },
    taskName: {
        fontSize: 14,
        color: theme.colors.text,
        fontFamily: theme.fonts.heading,
        flex: 1,
        paddingRight: 12,
    },
    assigneesContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 8,
        maxWidth: '60%',
        justifyContent: 'flex-end',
    },
    assigneeChip: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        paddingHorizontal: 8,
        paddingVertical: 4,
        borderRadius: 8,
        backgroundColor: theme.colors.surfaceVariant,
    },
    assigneeName: {
        fontSize: 12,
        color: theme.colors.text,
        maxWidth: 100,
    },
    addAssigneeChip: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
        paddingHorizontal: 8,
        paddingVertical: 4,
        borderRadius: 8,
        borderWidth: 1,
        borderStyle: 'dashed',
        borderColor: theme.colors.primary,
    },
    addAssigneeText: {
        fontSize: 12,
        color: theme.colors.primary,
        fontFamily: theme.fonts.heading,
    },
}));
